// src/pages/LoginPage.jsx
import { useState, useEffect } from "react";
import api from "../api/client";
import {
  setAccessToken,
  clearSession,
  consumeLoginError,
  getLastLoginEmail,
  setLastLoginEmail,
  getUser,
} from "../utils/session";
import "../styles/login.css";

import jadeLogo from "../assets/jade.png"; // Jade Global logo

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";
const IS_DEV = import.meta.env.VITE_DEV_MODE === "true" || import.meta.env.DEV;

export default function LoginPage() {
  const [devEmail, setDevEmail] = useState("");
  const [devError, setDevError] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [checkingSSO, setCheckingSSO] = useState(true);

  useEffect(() => {
    // ── TWO-WAY SSO: STATUS CHECK ──
    // Check if the user is logged into another ecosystem app (e.g. Testing App).
    // The backend will check if the shared `google_id_token` cookie is present.
    const checkStatus = () => {
      api
        .get("/auth/sso/status")
        .then((res) => {
          if (res.data.has_sso) {
            window.location.href = `${API_BASE}/auth/google/login`;
          } else {
            setCheckingSSO(false);
          }
        })
        .catch(() => setCheckingSSO(false));
    };

    checkStatus();
    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleDevLogin = async () => {
    setDevError("");
    if (!devEmail) {
      setDevError("Enter your email.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/auth/dev-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: devEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDevError(data.detail?.message || data.detail || "Login failed.");
        return;
      }
      // The dev-login response is a flat token payload (TokenResponse —
      // same shape the Google OAuth flow returns), no nested `user` object.
      // Store the token and let AuthContext's resolveInitialUser()/
      // userFromToken() derive the cached user from the token itself on
      // next mount (see AuthContext.jsx, and SsoCallback.jsx's login()).
      setAccessToken(data.access_token);
      if (data.email) setLastLoginEmail(data.email);
      window.location.href = "/";
    } catch (err) {
      setDevError(err?.message || "Could not reach backend.");
    }
  };

  // Check for deactivation or SSO error message (clears after reading so it only shows once)
  const loginError = consumeLoginError();

  // Check URL params for SSO redirect errors (e.g. ?error=deactivated)
  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get("error") || "";

  const displayError = loginError || urlError;

  // Try in this order:
  // 1) last_login_email (set by SsoCallback)
  // 2) user.email (from last session)
  // 3) default placeholder
  const lastEmail = getLastLoginEmail() || getUser()?.email || "user@jadeglobal.com";

  const initials = lastEmail ? lastEmail[0].toUpperCase() : "U";

  const handleLogin = () => {
    if (isSigningIn) return;
    setIsSigningIn(true);
    clearSession();

    window.location.href = `${API_BASE}/auth/google/login`;
  };

  return (
    <div className="sow-page">
      {/* FULL-WIDTH HEADER */}
      <header className="sow-login-header">
        <div className="sow-header-inner">
          <div className="sow-header-left">
            <img src={jadeLogo} alt="Jade Global" className="sow-jade-logo" />
            <div className="sow-header-title-block">
              <div className="sow-header-title">Jade Orion</div>
              <div className="sow-header-subtitle">
                Unified Intelligent System for end-to-end software delivery
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* BODY: dark strip + centered card */}
      <div className="sow-login-body">
        <aside className="sow-login-sidebar" />
        <main className="sow-login-main">
          <div className="sow-login-card">
            <div className="sow-auth-logo">
              {/* optional logo image */}
              {/* <img src={sowLogo} alt="SOW Analyzer" className="sow-auth-img" /> */}
            </div>

            <h1 className="sow-auth-title">Sign in with Google</h1>
            <p className="sow-auth-subtitle">
              Use your <strong>@jadeglobal.com</strong> Google account to
              continue.
            </p>

            {displayError && (
              <div className="sow-login-error">{displayError}</div>
            )}

            <button
              className="sow-google-btn"
              disabled={isSigningIn}
              onClick={handleLogin}
            >
              <div className="sow-google-left">
                <div className="sow-google-avatar">{initials}</div>
                <div className="sow-google-text">
                  <span className="sow-google-label">Sign in as</span>
                  <span className="sow-google-email">{lastEmail}</span>
                </div>
              </div>

              <div className="sow-google-icon">
                <span className="g-seg g-red" />
                <span className="g-seg g-yellow" />
                <span className="g-seg g-green" />
                <span className="g-seg g-blue" />
              </div>
            </button>

            <p className="sow-auth-footnote">
              Access is restricted to Jade Global employees. Your identity is
              verified via Google SSO.
            </p>

            {IS_DEV && (
              <div
                style={{
                  marginTop: "24px",
                  borderTop: "1px dashed #555",
                  paddingTop: "16px",
                }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    color: "#aaa",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Dev login (local only)
                </p>
                <input
                  type="email"
                  placeholder="your@jadeglobal.com"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleDevLogin()}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #444",
                    background: "#1e1e1e",
                    color: "#fff",
                    fontSize: "13px",
                    marginBottom: "8px",
                    boxSizing: "border-box",
                  }}
                />
                {devError && (
                  <p
                    style={{
                      color: "#f87171",
                      fontSize: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    {devError}
                  </p>
                )}
                <button
                  onClick={handleDevLogin}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#374151",
                    color: "#fff",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Login as this user
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
